import {Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {MessageService} from 'primeng/api';
import {AppointmentType} from '../../appointment/data/appointmentType.enum';
import {AppointmentDto} from '../../appointment/data/appointmentDto';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {LogLevel} from '../../logging/data/logLevel';
import {LogSourceType} from '../../logging/data/logSourceType';
import {LogSource} from '../../logging/data/logSource';
import {AppointmentReminderTime} from '../../appointment/data/appointmentRenderTime.enum';
import {Appointment} from '../../appointment/data/appointment';
import {AppStateService} from '../../app-common/services/appState/app-state.service';
import {SignalRService} from '../../app-common/services/signalr/signal-r.service';
import {AppointmentService} from '../../appointment/services/appointment.service';
import {UserService} from '../../user/services/user/user.service';
import {LoggerService} from '../../logging/services/logger.service';
import {CrudActivity} from '../../activity-log/data/crudActivity';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {SearchService} from "../../app-common/services/search/search.service";

@Component({
  selector: 'app-appointment-notification',
  templateUrl: './appointment-notification.component.html',
  styleUrls: ['./appointment-notification.component.scss'],
  animations: [
    trigger('fadein', [
      transition('void => *', [
        style({ opacity: 0, marginTop: '20px' }),
        animate(500, style({opacity: 1, marginTop: 0}))
      ])
    ]),
    trigger('openClose', [
      state('open', style({ transform: 'translateX(0)'
      })),
      state('closed', style({transform: 'translateX(500px)'
      })),
      transition('open => closed', [
        animate('0.25s')
      ]),
      transition('closed => open', [
        animate('0.25s')
      ])
    ])
  ]
})
export class AppointmentNotificationComponent implements OnInit {

  @Input() dto: AppointmentDto;

  @Output() deletion: EventEmitter<void> = new EventEmitter<void>();

  deleted: boolean = false;

  constructor(private domSanitizer: DomSanitizer,
              private signalRService: SignalRService,
              private messageService: MessageService,
              private appointmentService: AppointmentService,
              private appStateService: AppStateService,
              private userService: UserService,
              private loggingService: LoggerService,
              private elementRef: ElementRef,
              private searchService: SearchService) {
  }

  ngOnInit(): void {
    this.subscribeToSignalR();
  }

  getDueTime(): string {
    if (this.dto.appointment.dueDate) {
      return this.dto.appointment.dueDate.toLocaleString();
    }
    return '';
  }

  getAppointmentType(): string {
    return AppointmentType[this.dto.appointment.appointmentType];
  }

  getRemainingTime(): string {
    const today = new Date();
    let reminderTime: Date;
    reminderTime = new Date(this.dto.appointment.dueDate);

    // @ts-ignore
    let delta = Math.abs(reminderTime - today) / 1000;
    const daysDiff = Math.floor(delta / 86400);
    delta -= daysDiff * 86400;
    const hoursDiff = Math.floor(delta / 3600) % 24;
    delta -= hoursDiff * 3600;
    const minutesDiff = Math.floor(delta / 60) % 60;
    delta -= minutesDiff * 60;

    if (reminderTime.getDay() === today.getDay()) {
      return '<b class ="time-left">Today</b>.';
    }

    if (reminderTime.getDay() !== today.getDay() && daysDiff === 0) {
      return 'in <b class ="time-left">' + hoursDiff + '</b> hours <b class ="time-left">' + minutesDiff + '</b> minutes from now.';
    }

    if (daysDiff > 5) {
      return 'in <b class ="time-left">' + daysDiff + '</b> days from now.';
    }
    return 'in <b class ="time-left">' + daysDiff + '</b> days <b class ="time-left">' + hoursDiff + '</b> hours <b class ="time-left">' + minutesDiff + '</b> minutes from now.';
  }


  getReminder(): SafeHtml {
    const appointmentType = this.getAppointmentType();
    const remainingTime = this.getRemainingTime();

    switch (appointmentType) {
      case 'Reminder': {
        return this.domSanitizer.bypassSecurityTrustHtml('<b>Reminder</b> set ' + remainingTime);
      }
      case 'Call': {
        return this.domSanitizer.bypassSecurityTrustHtml('You have a <b>Call</b> ' + remainingTime);
      }
      case 'Meeting': {
        return this.domSanitizer.bypassSecurityTrustHtml('You have a <b>Meeting</b> ' + remainingTime);
      }
      case 'Email': {
        return this.domSanitizer.bypassSecurityTrustHtml('Remember to send <b>Email</b> ' + remainingTime);
      }
      case 'Task': {
        return this.domSanitizer.bypassSecurityTrustHtml('<b>Task</b> starts ' + remainingTime);
      }
      case 'Appointment': {
        return this.domSanitizer.bypassSecurityTrustHtml('Your appointment starts in ' + remainingTime);
      }
    }
  }

  getNotes(): string {
    if (this.dto.appointment.notes && this.dto.appointment.notes.length > 90) {
      return this.dto.appointment.notes.substr(0, 110) + '...';
    }
    return this.dto.appointment.notes;
  }

  async dismiss(): Promise<void> {
    await this.appointmentService.dismissAppointment(this.dto.appointment.id).finally(() => {
      this.delete();
      this.loggingService.logMessage(LogLevel.INFO, LogSourceType.COMPONENT, LogSource.AppointmentNotification, 'Dismissed appointment with id ' + this.dto.appointment.id);
      this.appointmentService.triggerRefreshAppointment();
    });
  }

  snooze(): void {
    this.appointmentService.getAppointmentById(this.dto.appointment.id).then(appointmentDto => {
      this.appointmentService.snooze(appointmentDto.appointment.id).then(appointmentDtoSnoozed => {
        if (appointmentDtoSnoozed.appointment.reminderTime !== AppointmentReminderTime.On_Time) {
          this.delete();
          this.loggingService.logMessage(LogLevel.INFO, LogSourceType.COMPONENT, LogSource.AppointmentNotification, 'Snoozed appointment with id ' + this.dto.appointment.id);
          this.appointmentService.triggerRefreshAppointment();
        }
      });
    });
  }

  showSnooze(): boolean {
    return this.dto.appointment.reminderTime !== AppointmentReminderTime.On_Time;
  }

  delete(): void {
    this.deleted = true;
    setTimeout(() => {
      this.deletion.emit();
    }, 250);
  }

  private subscribeToSignalR() {
    this.signalRService.crudHandler.appointmentEvents.update.subscribe( async (activity: CrudActivity) => {
      if (activity.entityId !== this.dto.appointment.id) {
        return;
      }

      await this.appointmentService.getAppointmentById(activity.entityId).then(appointmentDto => {
        const eventEmitterFn = () => {
          if (appointmentDto.appointment.dismissed) {
            this.delete();
          }
        };
        this.searchService.searchAndEmit(null, appointmentDto.appointment, eventEmitterFn, null);
      });
    });
    this.signalRService.crudHandler.appointmentEvents.delete.subscribe( async (activity: CrudActivity) => {
      if (activity.entityId !== this.dto.appointment.id) {
        return;
      }
      this.delete();
    });
  }
}


